import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from 'lucide-react';
import type { Me } from '../api/auth';
import { familyInfoApi, infoApi } from '../api/info';
import type { InfoPage, InfoSection } from '../api/info';
import { ValidationFailure } from '../api/events';
import type { FieldErrors } from '../api/events';
import { formatTimestamp } from '../lib/datetime';
import { AnnouncementAttachments, useKeepAttachmentsSigned } from '../ui/AnnouncementAttachments';
import { AppHeader } from '../ui/AppHeader';
import { AttachmentDraftsEditor, keptDrafts, RowAction, useAttachmentDrafts } from '../ui/AttachmentDrafts';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { CuratedText } from '../ui/CuratedText';
import { CuratedTextEditor } from '../ui/CuratedTextEditor';
import { EmptyState } from '../ui/EmptyState';
import { TextField } from '../ui/Field';

/** A section's attachments, documents and links together — `UnitInfoSection.MaxAttachments`. */
const SECTION_ATTACHMENT_LIMIT = 10;

/** A section heading's length — `UnitInfoSection.MaxTitleLength`. */
const SECTION_TITLE_MAX = 120;

/**
 * A unit's Info page — TROOP-256. The things a family asks every year, written down once: where
 * the unit meets, dues, the uniform, the medical form, who to call.
 *
 * **Both doors, one screen** (rule 13b). With a `unitId` it is the member's page for that unit,
 * read from `/units/{unit}/info`; without one it is a parent's, read from `/family/info` and
 * spanning every unit their children are in, each page under its unit's name.
 *
 * **Who may change it is the server's answer** (`canEdit`, from `UnitInfoAccessPolicy`) — officers
 * and admins, youth officers included — and every managing control is gated on the unit too,
 * since `unitId` is absent on the family door and every write is unit-scoped.
 *
 * **An empty page is not drawn as one.** An editor is offered "Add your first section"; anybody
 * else is told in a line that nothing has been written yet, rather than shown an empty frame.
 */
export function InfoScreen({
  unitId,
  me,
  onNavigate,
}: {
  /** The member's door. Absent on a parent's, which reads every unit their children are in. */
  unitId?: string;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [pages, setPages] = useState<InfoPage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPages(unitId ? [await infoApi.page(unitId)] : await familyInfoApi.pages());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the page.');
    }
  }, [unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Every document link stays signed while the page is open -- people leave this one open. Memoised
  // on the pages, so the re-sign timer is set once per read rather than once per render.
  const sections = useMemo(() => (pages ? pages.flatMap((p) => p.sections) : null), [pages]);
  const refresh = useCallback(() => void load(), [load]);
  useKeepAttachmentsSigned(sections, refresh);

  const familyDoor = unitId === undefined;
  const shown = familyDoor ? (pages ?? []).filter((p) => p.sections.length > 0) : (pages ?? []);
  const unitName = familyDoor ? null : pages?.[0]?.unitName;

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {familyDoor ? 'Your family' : (unitName ?? ' ')}
            </p>
            <h1 className="t-heading" style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 0' }}>
              Info
            </h1>
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 0', maxWidth: '52ch' }}
            >
              {familyDoor
                ? 'What each unit your children are in has written down for families: where it meets, what it costs, what to wear, who to ask.'
                : 'What the unit has written down for families: where it meets, what it costs, what to wear, who to ask.'}
            </p>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div className="flex flex-col" style={{ paddingBlock: 'var(--space-6)', gap: 'var(--space-7)' }}>
            {error && (
              <p
                className="t-body"
                role="alert"
                style={{
                  background: 'var(--color-status-blocked-surface)',
                  border: '1px solid var(--color-status-blocked)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  margin: 0,
                  color: 'var(--color-text-primary)',
                }}
              >
                {error}
              </p>
            )}

            {pages && shown.length === 0 && familyDoor && (
              <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                None of your children&rsquo;s units has written anything here yet.
              </p>
            )}

            {shown.map((page) => (
              <UnitPage
                key={page.unitId}
                page={page}
                // A managing control on a screen both doors reach is gated on the unit too: the
                // family door has no unit, and every write is unit-scoped.
                unitId={familyDoor ? null : unitId}
                showUnitName={familyDoor}
                onChanged={() => void load()}
              />
            ))}
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

function UnitPage({
  page,
  unitId,
  showUnitName,
  onChanged,
}: {
  page: InfoPage;
  unitId: string | null;
  showUnitName: boolean;
  onChanged: () => void;
}) {
  const canManage = page.canEdit && unitId !== null;
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);

  // The page re-reads itself while open, to keep document links signed. If somebody else removed
  // the section being edited in the meantime, the editor would vanish with the draft in it and no
  // word said -- so it closes with one.
  const editingGone =
    editing !== null && editing !== 'new' && !page.sections.some((s) => s.id === editing);

  useEffect(() => {
    if (editingGone) {
      setEditing(null);
      setOrderError(
        'Somebody else removed the section you were editing, so your changes to it were not saved.',
      );
    }
  }, [editingGone]);

  async function move(index: number, delta: -1 | 1) {
    if (!unitId) return;
    const ids = page.sections.map((s) => s.id);
    const target = index + delta;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setOrderError(null);

    try {
      await infoApi.reorder(unitId, ids);
    } catch (e) {
      setOrderError(
        e instanceof ValidationFailure
          ? (Object.values(e.errors)[0]?.[0] ?? 'Could not move it.')
          : e instanceof Error
            ? e.message
            : 'Could not move it.',
      );
    }

    onChanged();
  }

  return (
    <section className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
      {showUnitName && (
        <h2 className="t-subheading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          {page.unitName}
        </h2>
      )}

      {page.sections.length === 0 && !canManage && (
        <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Nothing has been written here yet. Your unit&rsquo;s leaders add to it.
        </p>
      )}

      {page.sections.length === 0 && canManage && editing !== 'new' && (
        <EmptyState
          icon={BookOpen}
          headline="Nothing written yet"
          explanation="Start with what families ask most: where you meet, what it costs, what to wear."
          action={{ label: 'Add your first section', onClick: () => setEditing('new') }}
        />
      )}

      {orderError && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
          {orderError}
        </p>
      )}

      {page.sections.map((section, index) =>
        editing === section.id && unitId ? (
          <SectionEditor
            key={section.id}
            unitId={unitId}
            section={section}
            onDone={() => {
              setEditing(null);
              onChanged();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <SectionCard
            key={section.id}
            section={section}
            manage={
              canManage && unitId && editing === null
                ? {
                    unitId,
                    onEdit: () => setEditing(section.id),
                    onMoveUp: index > 0 ? () => void move(index, -1) : undefined,
                    onMoveDown: index < page.sections.length - 1 ? () => void move(index, 1) : undefined,
                    onRemoved: onChanged,
                  }
                : undefined
            }
          />
        ),
      )}

      {canManage && unitId && editing === 'new' && (
        <SectionEditor
          unitId={unitId}
          onDone={() => {
            setEditing(null);
            onChanged();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {canManage && editing === null && page.sections.length > 0 && (
        <div>
          <Button variant="secondary" onClick={() => setEditing('new')}>
            <Plus size={16} aria-hidden="true" />
            Add a section
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * One section as a family reads it. SPEC-GAP: no Info section in the design system; built as a
 * raised card with the subheading step for its title, `CuratedText` for its body and the
 * announcement's attachment rows beneath — the same three things an announcement card shows.
 */
function SectionCard({
  section,
  manage,
}: {
  section: InfoSection;
  manage?: {
    unitId: string;
    onEdit: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    onRemoved: () => void;
  };
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    if (!manage) return;
    setBusy(true);
    try {
      await infoApi.removeSection(manage.unitId, section.id);
      manage.onRemoved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove it.');
      setBusy(false);
    }
  }

  return (
    <article
      className="flex flex-col"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4) var(--space-5)',
        gap: 'var(--space-3)',
      }}
    >
      <div className="flex items-start" style={{ gap: 'var(--space-2)' }}>
        <h3
          className="t-subheading"
          style={{ color: 'var(--color-text-primary)', margin: 0, flex: '1 1 8rem', minWidth: '6rem', overflowWrap: 'anywhere' }}
        >
          {section.title}
        </h3>

        {manage && (
          <>
            {manage.onMoveUp && <RowAction label={`Move ${section.title} up`} onClick={manage.onMoveUp} icon={ChevronUp} />}
            {manage.onMoveDown && (
              <RowAction label={`Move ${section.title} down`} onClick={manage.onMoveDown} icon={ChevronDown} />
            )}
            <RowAction label={`Edit ${section.title}`} onClick={manage.onEdit} icon={Pencil} />
            <RowAction label={`Remove ${section.title}`} onClick={() => setConfirming(true)} icon={Trash2} />
          </>
        )}
      </div>

      {section.body && <CuratedText text={section.body} style={{ color: 'var(--color-text-primary)' }} />}

      <AnnouncementAttachments attachments={section.attachments} />

      <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
        Last changed {formatTimestamp(section.updatedAt)} by {section.updatedByDisplayName}
      </p>

      {/* An inline question rather than a browser dialog. The section is kept in the unit's
          record either way -- this takes it off the page. */}
      {confirming && manage && (
        <div
          className="flex flex-col"
          style={{
            gap: 'var(--space-2)',
            borderTop: '1px solid var(--color-border-subtle)',
            paddingTop: 'var(--space-3)',
          }}
        >
          <p className="t-body" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
            Take &ldquo;{section.title}&rdquo; off the page? Families stop seeing it and what it hands out.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" onClick={() => void remove()} disabledReason={busy ? 'Removing it…' : undefined}>
              Remove it
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
          </div>
          {error && (
            <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
              {error}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

/** Adding a section, or changing one. The save names the whole attachment list, kept rows by id. */
function SectionEditor({
  unitId,
  section,
  onDone,
  onCancel,
}: {
  unitId: string;
  section?: InfoSection;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(section?.title ?? '');
  const [body, setBody] = useState(section?.body ?? '');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const drafts = useAttachmentDrafts(
    (file) => infoApi.uploadDocument(unitId, file),
    section ? keptDrafts(section.attachments, 0) : [],
  );

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (drafts.uploading) return;

    setBusy(true);
    setFieldErrors({});
    setError(null);

    const input = { title: title.trim(), body: body.trim(), attachments: drafts.toInput() };

    try {
      if (section) {
        await infoApi.saveSection(unitId, section.id, input);
      } else {
        await infoApi.addSection(unitId, input);
      }
      onDone();
    } catch (err) {
      if (err instanceof ValidationFailure) {
        setFieldErrors(err.errors);
      } else {
        setError(err instanceof Error ? err.message : 'Could not save it.');
      }
      setBusy(false);
    }
  }

  const incomplete = !title.trim() ? 'Give the section a heading first.' : (drafts.incomplete ?? (busy ? 'Saving…' : undefined));

  return (
    <form
      onSubmit={save}
      className="flex flex-col"
      aria-label={section ? `Editing ${section.title}` : 'A new section'}
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4) var(--space-5)',
        gap: 'var(--space-5)',
      }}
    >
      <TextField
        label="Heading"
        required
        value={title}
        maxLength={SECTION_TITLE_MAX}
        onChange={(e) => setTitle(e.currentTarget.value)}
        error={fieldErrors.title?.[0]}
        hint="What a family would look for: Where we meet, Dues, Uniform, Who to call."
      />

      <CuratedTextEditor
        label="What it says"
        value={body}
        onChange={setBody}
        error={fieldErrors.body?.[0]}
        hint="The buttons type the formatting for you. A link shows families the site it goes to, and only https:// links are accepted."
      />

      <AttachmentDraftsEditor
        drafts={drafts}
        limit={SECTION_ATTACHMENT_LIMIT}
        atLimitMessage="That is as many as one section can carry. Remove one, or put the rest in a section of their own."
        error={fieldErrors.attachments?.[0]}
      >
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Up to {SECTION_ATTACHMENT_LIMIT} &mdash; documents or links. A document is a PDF or a picture, and
          goes in here rather than as a link in the text, so it opens for every family.
        </p>
      </AttachmentDraftsEditor>

      {error && (
        <p
          className="t-body"
          role="alert"
          style={{
            background: 'var(--color-status-blocked-surface)',
            border: '1px solid var(--color-status-blocked)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3)',
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Button variant="primary" type="submit" disabledReason={incomplete}>
          {section ? 'Save' : 'Add it'}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
